import { ArrowLeft, Octagon, Pause, Play, Radio, RotateCcw, SkipForward, Tv, XOctagon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getJob, JobDetailResponse, RunSummary, stopJob } from "../api/jobs";
import { LogRow, queryLogs } from "../api/logs";
import { abortRun, pauseRun, resumeRun, type ResumeMode } from "../api/runs";
import { fetchFailureScreenshot, getFailure, type ScriptFailure } from "../api/failures";
import { StatusBadge } from "../components/StatusBadge";
import { formatDateTime, relativeTime } from "../utils/format";
import { type RealtimeEvent, useRealtimeChannel } from "../hooks/useRealtimeChannel";
import { useAuth } from "../hooks/useAuth";
import { useTranslation } from "react-i18next";

const LOG_LEVEL_COLORS: Record<string, string> = {
  debug: "text-ink-500",
  info: "text-sky-300",
  warn: "text-amber-300",
  error: "text-red-300",
};

export function JobDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<JobDetailResponse | null>(null);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState<"runs" | "logs" | "script">("runs");

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const j = await getJob(id);
      setData(j);
      const filtered = await queryLogs({ phone_id: j.job.phone_id, limit: 300 });
      setLogs(filtered);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Realtime : subscribe room job:<id> ET phone:<phone_id> (logs)
  const rooms = useMemo(() => {
    const r = [];
    if (id) r.push(`job:${id}`);
    if (data?.job.phone_id) r.push(`phone:${data.job.phone_id}`);
    return r;
  }, [id, data?.job.phone_id]);

  const onEvent = useCallback(
    (evt: RealtimeEvent) => {
      if (evt.event === "phone:log") {
        setLogs((cur) => {
          // Évite les doublons sur reload + push
          const newRow: LogRow = {
            id: `realtime-${Date.now()}-${Math.random()}`,
            phone_id: evt.payload.phone_id,
            run_id: evt.payload.run_id,
            level: evt.payload.level,
            source: evt.payload.source,
            message: evt.payload.message,
            metadata: null,
            created_at: evt.payload.created_at,
          };
          return [newRow, ...cur].slice(0, 500);
        });
      } else if (evt.event === "run:update" || evt.event === "job:update") {
        // Recharge le job complet pour avoir runs[] et stats à jour
        void reload();
      }
    },
    [reload]
  );
  const { connected } = useRealtimeChannel(rooms, onEvent);

  // Stop = annulation du job entier (et de son run en cours). La pause/reprise
  // se fait au niveau du RUN (cf. pauseActiveRun + PausedRunCard), pas du job.
  async function handleStop() {
    if (!id) return;
    setActionLoading(true);
    try {
      await stopJob(id);
      await reload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setActionLoading(false);
    }
  }

  // Pause MANUELLE on-demand : gèle le run en cours d'exécution sur le device
  // (≠ pause job qui ne fait que stopper la planification loop). Le run repasse
  // ensuite par le bandeau PausedRunCard (bouton Continue) pour la reprise.
  async function pauseActiveRun(runId: string) {
    setActionLoading(true);
    try {
      await pauseRun(runId);
      await reload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) return <div className="p-8 text-ink-500">Chargement…</div>;
  if (error || !data) return <div className="p-8 text-red-400">{error ?? "Job introuvable"}</div>;

  const { job, runs, stats } = data;
  // Run actuellement en exécution (cible de la pause manuelle on-demand).
  const runningRun = runs.find((r) => r.status === "running");
  const isActive = job.status === "running" || job.status === "paused" || job.status === "pending";
  const progressText = job.max_runs
    ? `${job.current_run_count} / ${job.max_runs}`
    : `${job.current_run_count} / ∞`;
  const progressPct = job.max_runs
    ? Math.min(100, Math.round((job.current_run_count / job.max_runs) * 100))
    : null;

  // Filtre les logs sur les run_id de ce job
  const runIds = new Set(runs.map((r) => r.id));
  const jobLogs = logs.filter((l) => l.run_id && runIds.has(l.run_id));

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <button
            onClick={() => navigate("/jobs")}
            className="p-2 rounded-md text-ink-400 hover:bg-ink-700/50 hover:text-white transition-colors mt-1"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold text-white truncate">{job.name}</h1>
              <StatusBadge status={job.status} />
              <span className="px-2 py-0.5 rounded-md bg-ink-700/60 text-xs text-ink-300">
                {job.mode}
              </span>
            </div>
            <div className="mt-2 text-sm text-ink-500 flex items-center gap-4 flex-wrap">
              <span>
                Script : <button
                  onClick={() => navigate(`/scripts/${job.script_id}`)}
                  className="text-velvet-300 hover:underline"
                >{job.script_name}</button>
              </span>
              <span>
                Téléphone : <button
                  onClick={() => navigate("/phones")}
                  className="text-velvet-300 hover:underline"
                >{job.phone_name}</button>
              </span>
              <span>Créé {relativeTime(job.created_at)}</span>
              {job.ended_at && <span>Terminé {relativeTime(job.ended_at)}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border mr-2 ${
              connected
                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                : "bg-ink-700/40 text-ink-500 border-ink-600"
            }`}
          >
            <Radio className={`size-3 ${connected ? "animate-pulse" : ""}`} />
            {connected ? t("jobs.detail.live") : t("jobs.detail.offline")}
          </div>
          {runningRun && (
            <button
              onClick={() => void pauseActiveRun(runningRun.id)}
              disabled={actionLoading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              <Pause className="size-4" />
              {t("jobs.detail.pause")}
            </button>
          )}
          {isActive && (
            <button
              onClick={() => {
                if (window.confirm(t("jobs.detail.confirmStop"))) {
                  void handleStop();
                }
              }}
              disabled={actionLoading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              <Octagon className="size-4" />
              {t("jobs.detail.stop")}
            </button>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label={t("jobs.detail.stats.progress")} value={progressText} sub={progressPct !== null ? `${progressPct}%` : undefined} />
        <StatCard label={t("jobs.detail.stats.success")} value={`${stats.success ?? 0}`} accent="text-emerald-400" />
        <StatCard label={t("jobs.detail.stats.failed")} value={`${stats.failed ?? 0}`} accent="text-red-400" />
        <StatCard label={t("jobs.detail.stats.cancelled")} value={`${stats.cancelled ?? 0}`} accent="text-ink-400" />
        <StatCard label={t("jobs.detail.stats.pending")} value={`${(stats.pending ?? 0) + (stats.dispatched ?? 0)}`} accent="text-sky-400" />
      </div>

      {/* Bandeau "Run en pause" : visible UNIQUEMENT si un run est paused.
          On choisit le plus récent : il y en a normalement 1 seul à la fois
          (un tel ne fait qu'un run en parallèle), mais on est défensif. */}
      {runs.filter((r) => r.status === "paused").map((r) => (
        <PausedRunCard key={r.id} run={r} phoneId={job.phone_id} onResolved={() => void reload()} />
      ))}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-ink-700">
        {(isAdmin
          ? (["runs", "logs", "script"] as const)
          : (["runs", "logs"] as const)
        ).map((tabKey) => (
          <button
            key={tabKey}
            onClick={() => setTab(tabKey)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === tabKey
                ? "border-velvet-500 text-velvet-300"
                : "border-transparent text-ink-500 hover:text-white"
            }`}
          >
            {tabKey === "runs"
              ? `${t("jobs.detail.tabs.runs")} (${runs.length})`
              : tabKey === "logs"
                ? `${t("jobs.detail.tabs.logs")} (${jobLogs.length})`
                : t("jobs.detail.tabs.script")}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "runs" && <RunsTable runs={runs} />}
      {tab === "logs" && <LogsView logs={jobLogs} />}
      {tab === "script" && <ScriptView content={job.script_content} />}
    </div>
  );
}

// Bandeau dépliable pour un run en `paused` :
//   - charge la failure liée (runs.paused_failure_id) pour avoir le contexte
//     (step, error_message, screenshot...)
//   - 3 boutons : Retry (re-exec le step), Skip (passe au suivant), Abort
//   - Un clic dispatch côté hub puis on reload le job pour refléter l'update
function PausedRunCard({ run, phoneId, onResolved }: { run: RunSummary; phoneId: string; onResolved: () => void }) {
  const { t } = useTranslation();
  const [failure, setFailure] = useState<ScriptFailure | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Pause manuelle on-demand vs pause-sur-échec : on distingue via la présence
  // d'une failure liée. Manuelle → bouton Continue ; échec → retry/skip + snapshot.
  const isManual = !run.paused_failure_id;

  useEffect(() => {
    let cancelled = false;
    let revokeUrl: string | null = null;
    async function load() {
      try {
        if (run.paused_failure_id) {
          const f = await getFailure(run.paused_failure_id);
          if (cancelled) return;
          setFailure(f);
          if (f.has_screenshot) {
            const url = await fetchFailureScreenshot(f.id);
            if (cancelled) {
              URL.revokeObjectURL(url);
              return;
            }
            revokeUrl = url;
            setScreenshotUrl(url);
          }
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : t("common.error"));
      }
    }
    void load();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [run.paused_failure_id, t]);

  async function resume(mode: ResumeMode) {
    setBusy(mode);
    setErr(null);
    try {
      await resumeRun(run.id, mode);
      onResolved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(null);
    }
  }
  async function abort() {
    if (!window.confirm(t("jobs.detail.pausedBanner.confirmAbort"))) return;
    setBusy("abort");
    setErr(null);
    try {
      await abortRun(run.id);
      onResolved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Pause className="size-5 text-amber-300 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-amber-200 font-semibold">
              {isManual
                ? t("jobs.detail.pausedBanner.manualTitle")
                : t("jobs.detail.pausedBanner.title")}
            </div>
            <div className="text-sm text-amber-100/80 mt-1">
              {!isManual && (
                <>
                  {t("jobs.detail.pausedBanner.stepLabel")}{" "}
                  <code className="px-1 py-0.5 rounded bg-ink-800 text-amber-200">{run.paused_step_path ?? "?"}</code>
                  {" · "}
                </>
              )}
              <span className="text-ink-400">{t("jobs.detail.pausedBanner.runLabel")} {run.id.slice(0, 8)}…</span>
              {run.paused_at && (
                <span className="text-ink-500 ml-2">
                  {t("jobs.detail.pausedBanner.pausedSince", { ago: relativeTime(run.paused_at) })}
                </span>
              )}
            </div>
            {failure?.error_message && (
              <div className="mt-2 text-xs text-red-300 break-words max-w-2xl">
                {failure.error_message}
              </div>
            )}
            <div className="mt-2 text-xs text-ink-500">
              {isManual
                ? t("jobs.detail.pausedBanner.manualHint")
                : t("jobs.detail.pausedBanner.autoAbort")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isManual ? (
            <>
              <button
                onClick={() => window.open(`/phones/${phoneId}/stream`, "_blank", "noopener")}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium transition-colors"
              >
                <Tv className="size-4" />
                {t("jobs.detail.pausedBanner.openStream")}
              </button>
              <button
                onClick={() => void resume("continue")}
                disabled={busy !== null}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
              >
                <Play className="size-4" />
                {busy === "continue" ? "…" : t("jobs.detail.pausedBanner.continue")}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => void resume("retry")}
                disabled={busy !== null}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
              >
                <RotateCcw className="size-4" />
                {busy === "retry" ? "…" : t("jobs.detail.pausedBanner.retry")}
              </button>
              <button
                onClick={() => void resume("skip")}
                disabled={busy !== null}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
              >
                <SkipForward className="size-4" />
                {busy === "skip" ? "…" : t("jobs.detail.pausedBanner.skip")}
              </button>
            </>
          )}
          <button
            onClick={() => void abort()}
            disabled={busy !== null}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
          >
            <XOctagon className="size-4" />
            {busy === "abort" ? "…" : t("jobs.detail.pausedBanner.abort")}
          </button>
        </div>
      </div>

      {err && (
        <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
          {err}
        </div>
      )}

      {screenshotUrl && (
        <div className="border border-ink-700 rounded-lg overflow-hidden bg-ink-900 max-w-md">
          <img
            src={screenshotUrl}
            alt="Screenshot au moment du fail"
            className="w-full h-auto"
          />
          <div className="text-xs text-ink-500 px-3 py-2 border-t border-ink-700">
            {t("jobs.detail.pausedBanner.screenshotHint")}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-800/60 p-4">
      <div className="text-xs uppercase tracking-wider text-ink-500">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${accent ?? "text-white"}`}>{value}</div>
      {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function RunsTable({ runs }: { runs: RunSummary[] }) {
  const { t } = useTranslation();
  if (runs.length === 0) {
    return <div className="text-center py-8 text-ink-500">{t("jobs.detail.runs.empty")}</div>;
  }
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-800/60 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-ink-700/40 text-ink-500 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left px-4 py-3">{t("jobs.detail.runs.columns.id")}</th>
            <th className="text-left px-4 py-3">{t("jobs.detail.runs.columns.status")}</th>
            <th className="text-left px-4 py-3">{t("jobs.detail.runs.columns.started")}</th>
            <th className="text-left px-4 py-3">{t("jobs.detail.runs.columns.duration")}</th>
            <th className="text-left px-4 py-3">{t("jobs.detail.runs.columns.result")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-700/50">
          {runs.map((r) => (
            <tr key={r.id} className="hover:bg-ink-700/20 transition-colors">
              <td className="px-4 py-3 text-ink-400 font-mono text-xs">{r.id.slice(0, 8)}…</td>
              <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3 text-ink-400">
                {r.started_at ? formatDateTime(r.started_at) : "—"}
              </td>
              <td className="px-4 py-3 text-ink-400">
                {r.duration_ms ? formatDuration(r.duration_ms) : "—"}
              </td>
              <td className="px-4 py-3 text-ink-400 max-w-md">
                {r.failure_reason ? (
                  <span className="text-red-300">
                    [{r.failure_reason}] {r.failure_detail}
                  </span>
                ) : (
                  <span className="truncate block">{r.result_summary ?? "—"}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LogsView({ logs }: { logs: LogRow[] }) {
  const { t } = useTranslation();
  if (logs.length === 0) {
    return (
      <div className="text-center py-8 text-ink-500">
        {t("jobs.detail.logs.empty")}
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-900/60 p-4 font-mono text-xs max-h-[60vh] overflow-auto">
      {[...logs].reverse().map((l) => (
        <div key={l.id} className="flex gap-3 py-0.5 border-b border-ink-800/50 last:border-0">
          <span className="text-ink-500 shrink-0">{formatDateTime(l.created_at)}</span>
          <span className={`shrink-0 w-12 ${LOG_LEVEL_COLORS[l.level] ?? "text-ink-500"}`}>
            [{l.level}]
          </span>
          <span className="text-ink-400 shrink-0">{l.source}</span>
          <span className="text-ink-300 break-all">{l.message}</span>
        </div>
      ))}
    </div>
  );
}

function ScriptView({ content }: { content: string }) {
  return (
    <pre className="rounded-xl border border-ink-700 bg-ink-900/60 p-4 text-xs font-mono text-ink-300 max-h-[60vh] overflow-auto">
      {content}
    </pre>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const rs = Math.round(s % 60);
  return `${m}m ${rs}s`;
}
